import os from 'os';
import path from 'path';

/**
 * Walk the current stack and return the first frame that is *not* inside
 * pino, fastify, this file, or node internals. Returns `{ Path?: string }`
 * where `Path` is `<absolute-file-path>:<line>`. Returns `{}` if no
 * meaningful user frame can be identified (e.g. logs originating from
 * Fastify's internal request lifecycle).
 */
// Set LOG_CALLER=false to skip stack inspection entirely in high-throughput environments.
const LOG_CALLER_ENABLED = process.env.LOG_CALLER !== 'false';
const CALLER_SKIP_PATTERNS = [
  // Our own logger machinery — always at the top of every stack.
  `${path.sep}apps${path.sep}apply${path.sep}lib${path.sep}logger${path.sep}`,
  // Third-party log/HTTP framework frames.
  `${path.sep}node_modules${path.sep}pino`,
  `${path.sep}node_modules${path.sep}fastify`,
  `${path.sep}node_modules${path.sep}@fastify${path.sep}`,
  `${path.sep}node_modules${path.sep}thread-stream`,
  `${path.sep}node_modules${path.sep}sonic-boom`,
  // Next.js dev/prod runtime. In dev, user API handler code is bundled
  // INTO these files (e.g. `pages-api.runtime.dev.js`), so a frame pointing
  // at them means we cannot resolve back to the real `.ts` source — do not
  // report it as `Source`.
  `${path.sep}node_modules${path.sep}next${path.sep}`,
  // Webpack bundle output paths — bundled server code with no useful line
  // reference until source-map-support remaps it. If a frame still points
  // here, we could not resolve back to real source; skip it.
  `${path.sep}apps${path.sep}apply${path.sep}tmp${path.sep}`,
  `${path.sep}apps${path.sep}apply${path.sep}.next${path.sep}`,
  `${path.sep}dist${path.sep}apps${path.sep}apply${path.sep}`,
  // Webpack-internal virtual URIs used by dev-mode source maps.
  'webpack:',
  'webpack-internal:',
  '(node:',
  'node:internal',
];
// Match `at <fn> (<file>:<line>:<col>)` or `at <file>:<line>:<col>`.
export const STACK_FRAME_RE = /^\s*at\s+(?:.*?\s+\()?(.+?):(\d+):(\d+)\)?\s*$/;

// Shared by server (extractCallerFields) and browser (remote.ts) to avoid duplicated
// stack-parsing logic. Each caller applies its own skip-pattern list on the result.
export function parseStackFrames(
  stack: string
): Array<{ file: string; line: string }> {
  const frames: Array<{ file: string; line: string }> = [];
  for (const rawLine of stack.split('\n')) {
    const m = STACK_FRAME_RE.exec(rawLine);
    if (!m || !m[1] || !m[2]) {
      continue;
    }
    frames.push({ file: m[1], line: m[2] });
  }
  return frames;
}

function extractCallerFields(): { Path?: string } {
  if (!LOG_CALLER_ENABLED) {
    return {};
  }
  const stack = new Error('log-caller').stack;
  if (!stack) {
    return {};
  }
  for (const { file, line } of parseStackFrames(stack)) {
    if (CALLER_SKIP_PATTERNS.some((p) => file.includes(p))) {
      continue;
    }
    return { Path: `${path.resolve(file)}:${line}` };
  }
  return {};
}

export const SERVICE_NAME_PATTERN = /^checkout(\.[a-z0-9][a-z0-9-]*)*$/;

export type ServiceName = 'checkout' | `checkout.${string}`;

export function assertServiceName(name: string): asserts name is ServiceName {
  if (!SERVICE_NAME_PATTERN.test(name)) {
    throw new Error(
      `Invalid logger service name "${name}". ` +
        `Must match "checkout" or "checkout.<functionality>" (lowercase, dot-separated).`
    );
  }
}

const RENAME_KEYS: Record<string, string> = {
  msg: 'Message',
  message: 'Message',
  time: 'TimeStamp',
  level: 'Level',
  name: 'Service',
  hostName: 'Host',
  spanId: 'SpanID',
  traceID: 'TraceID',
  requestID: 'RequestID',
  correlationId: 'CorrelationID',
  sessionId: 'SessionID',
  sessionID: 'SessionID',
  userAgent: 'UserAgent',
  browser: 'Browser',
  browserVersion: 'BrowserVersion',
  os: 'OS',
  viewport: 'Viewport',
  deviceType: 'DeviceType',
  pageUrl: 'PageUrl',
  route: 'Route',
  locale: 'Locale',
  currency: 'Currency',
  featureFlags: 'FeatureFlags',
  merchantID: 'MerchantID',
  buyerID: 'BuyerID',
  orderId: 'OrderID',
  cartId: 'CartID',
  programID: 'ProgramID',
  paymentMethod: 'PaymentMethod',
  // Fastify / HTTP request-completion fields
  responseTime: 'DurationMS',
  statusCode: 'StatusCode',
  method: 'Method',
  url: 'Url',
  host: 'Host',
  remoteAddress: 'RemoteAddress',
  remotePort: 'RemotePort',
  headers: 'Headers',
};

function renameKey(key: string): string {
  return RENAME_KEYS[key] ?? key;
}

export function getBaseFields(): Record<string, string> {
  const base: Record<string, string> = {
    Env:
      process.env.DD_ENV ||
      process.env.ENVIRONMENT ||
      process.env.NODE_ENV ||
      'unknown',
    Version:
      process.env.DD_VERSION ||
      process.env.APP_VERSION ||
      process.env.GIT_SHA ||
      process.env.BUILD_ID ||
      'unknown',
    Host: os.hostname(),
  };

  if (process.env.POD_NAME) {
    base.PodName = process.env.POD_NAME;
  }
  if (process.env.POD_IP) {
    base.InstanceIP = process.env.POD_IP;
  }
  if (process.env.CONTAINER_ID) {
    base.ContainerID = process.env.CONTAINER_ID;
  }
  if (process.env.NODE_NAME) {
    base.NodeName = process.env.NODE_NAME;
  }
  if (process.env.REGION) {
    base.Region = process.env.REGION;
  }
  if (process.env.AVAILABILITY_ZONE) {
    base.AvailabilityZone = process.env.AVAILABILITY_ZONE;
  }
  if (process.env.TENANT) {
    base.Tenant = process.env.TENANT;
  }

  return base;
}

/** Extract TraceID / SpanID from dd-trace's `dd` object, if present. */
function extractTraceFields(obj: Record<string, unknown>): {
  TraceID?: string;
  SpanID?: string;
} {
  const dd = obj.dd as
    | { trace_id?: string; span_id?: string; service?: string }
    | undefined;
  if (!dd) {
    return {};
  }
  const out: { TraceID?: string; SpanID?: string } = {};
  if (dd.trace_id) {
    out.TraceID = String(dd.trace_id);
  }
  if (dd.span_id) {
    out.SpanID = String(dd.span_id);
  }
  return out;
}

/**
 * Read client / device context fields from the Fastify request-context.
 * Returns an empty object on the browser or when the module can't be loaded
 * (e.g. background jobs, tests). Server code populates the context in the
 * Fastify `onRequest` hook via `setLogContextFromRequest`.
 */
function getContextFields(): Record<string, string | null> {
  if (typeof window !== 'undefined') {
    return {};
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { requestContext } = require('@fastify/request-context');
    const get = (k: string): string | null =>
      (requestContext.get(k) as string | null) ?? null;
    return {
      RequestID: get('requestID'),
      SessionID: get('sessionID'),
      UserAgent: get('userAgent'),
      Browser: get('browser'),
      BrowserVersion: get('browserVersion'),
      OS: get('os'),
      DeviceType: get('deviceType'),
      Viewport: get('viewport'),
      Locale: get('locale'),
      PageUrl: get('pageUrl'),
      Route: get('route'),
    };
  } catch {
    return {};
  }
}

/** Rename any known lower/camelCase keys in a record to PascalCase. */
function renameKeys(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (k === 'dd') {
      continue;
    }
    // Recursively rename keys inside plain sub-objects (e.g. req, res, headers).
    if (
      v !== null &&
      typeof v === 'object' &&
      !Array.isArray(v) &&
      v.constructor === Object
    ) {
      out[renameKey(k)] = renameKeys(v as Record<string, unknown>);
    } else {
      out[renameKey(k)] = v;
    }
  }
  return out;
}

/**
 * Keys we guarantee on every log line so the schema stays stable across
 * services and dashboards. Anything absent from the record is emitted as
 * `null` rather than being omitted. HTTP-completion fields (Method, Url,
 * StatusCode, ...) are intentionally NOT in this list — they are only
 * meaningful on request-completion lines and would be noise otherwise.
 *
 * `TraceID` is also intentionally NOT here: it is bound directly by Fastify
 * on the per-request child logger (via `requestIdLogLabel: 'TraceID'` in
 * `server/index.ts`) and is emitted from the pino bindings section. Adding
 * it here would produce a duplicate JSON key on every request-scoped line.
 * Non-request logs (startup, background jobs) simply won't carry a TraceID,
 * which correctly reflects that there is no trace to point at.
 */
const REQUIRED_LOG_KEYS = [
  // Tracing / correlation
  'SpanID',
  'RequestID',
  'CorrelationID',
  'SessionID',
  // Client / device context
  'UserAgent',
  'Browser',
  'BrowserVersion',
  'OS',
  'Viewport',
  'DeviceType',
  'PageUrl',
  'Route',
  'Locale',
  // Business identifiers
  'MerchantID',
  'BuyerID',
  'OrderID',
  'CartID',
  'ProgramID',
  'PaymentMethod',
] as const;

// Verbose levels suppressed in production unless overridden.
const VERBOSE_LEVELS = new Set(['debug', 'info']);

// Runtime override set by FeatureFlagsContext after flag evaluation (browser only).
// null = no flag evaluated yet; fall through to env gate.
let runtimeVerboseOverride: boolean | null = null;

// Called by FeatureFlagsContext once flags are fetched.
export function setRuntimeVerboseLogging(enabled: boolean): void {
  runtimeVerboseOverride = enabled;
}

// For test isolation only.
export function resetRuntimeVerboseLogging(): void {
  runtimeVerboseOverride = null;
}

export function isVerboseSuppressed(level: string): boolean {
  if (process.env.NODE_ENV !== 'production') {
    return false;
  }
  // Feature flag takes precedence over env so it can be toggled without pod restarts.
  if (runtimeVerboseOverride !== null) {
    return runtimeVerboseOverride ? false : VERBOSE_LEVELS.has(level);
  }
  if (process.env.ENABLE_VERBOSE_LOGGING === 'true') {
    return false;
  }
  return VERBOSE_LEVELS.has(level);
}

/** Shared Pino options — merge into every Pino instance we create. */
function buildSharedPinoOptions() {
  const base = getBaseFields();

  return {
    messageKey: 'Message',
    timestamp: () => `,"TimeStamp":"${new Date().toISOString()}"`,
    base,

    formatters: {
      level: (label: string): Record<string, unknown> => ({ Level: label }),
      // Rename known camelCase bindings to PascalCase. TraceID is bound by
      // Fastify at child-logger creation time via `requestIdLogLabel:
      // 'TraceID'` in `server/index.ts`, so it flows through here as the
      // single canonical source of the field. We intentionally do NOT
      // re-emit TraceID from the `log` formatter or the null-default list —
      // that would produce duplicate JSON keys (bindings + record).
      bindings: (b: Record<string, unknown>): Record<string, unknown> =>
        renameKeys(b),

      // Reshape each log record just before serialization.
      log: (obj: Record<string, unknown>): Record<string, unknown> => {
        const merged: Record<string, unknown> = {
          ...renameKeys(obj),
          ...extractTraceFields(obj),
          ...extractCallerFields(),
        };

        // Overlay client/device/request-context fields from the Fastify
        // request-context — only when the record itself doesn't already
        // carry that key with a real value.
        const ctx = getContextFields();
        for (const [k, v] of Object.entries(ctx)) {
          if (merged[k] == null && v != null) {
            merged[k] = v;
          }
        }

        // Guarantee every required key exists (`null` when still unknown)
        // so the emitted JSON has a stable schema.
        for (const key of REQUIRED_LOG_KEYS) {
          if (merged[key] == null) {
            merged[key] = null;
          }
        }
        return merged;
      },
    },
    redact: {
      paths: [
        'password',
        '*.password',
        'token',
        '*.token',
        'cvv',
        '*.cvv',
        'cvc',
        '*.cvc',
        'pan',
        '*.pan',
        'cardNumber',
        '*.cardNumber',
        'accountNumber',
        '*.accountNumber',
        'routingNumber',
        '*.routingNumber',
        'ssn',
        '*.ssn',
        'dob',
        '*.dob',
        'dateOfBirth',
        '*.dateOfBirth',
        'accessToken',
        '*.accessToken',
        'refreshToken',
        '*.refreshToken',
        'idToken',
        '*.idToken',
        'sessionToken',
        '*.sessionToken',
        'apiKey',
        '*.apiKey',
        'authorization',
        'headers.authorization',
        'headers.cookie',
        'headers["set-cookie"]',
        'BrowserCtx.headers.authorization',
        'BrowserCtx.headers.cookie',
        'req.headers.authorization',
        'req.headers.cookie',
        'req.headers["set-cookie"]',
        'res.headers["set-cookie"]',
        'err.stack',
        '*.stack',
        'CardNumber',
        'SSN',
      ],
      censor: '[REDACTED]',
    },
  };
}

export const sharedPinoOptions = buildSharedPinoOptions();

/**
 * Pino's browser build silently ignores `formatters`, so the numeric `level`
 * field (e.g. `50`) never gets converted to its string label (`error`) on the
 * client. This helper does just that one substitution and leaves every other
 * field of the raw browser pino record untouched.
 */
const PINO_LEVEL_LABELS: Record<number, string> = {
  10: 'trace',
  20: 'debug',
  30: 'info',
  40: 'warn',
  50: 'error',
  60: 'fatal',
};

export function labelBrowserLevel(
  rec: Record<string, unknown>
): Record<string, unknown> {
  const { level } = rec;
  if (typeof level === 'number' && PINO_LEVEL_LABELS[level]) {
    return { ...rec, level: PINO_LEVEL_LABELS[level] };
  }
  return rec;
}

// TODO(APEX-8578-followup): migrate to FeatureFlagClient once logger lazy-inits
// after XPropsProvider and /api/log receives merchantID/deviceID for FF context.
export const isBrowserLogForwardingEnabled = (): boolean => {
  if (process.env.NODE_ENV === 'production') {
    return process.env.NEXT_PUBLIC_FORWARD_BROWSER_LOGS === 'true';
  }
  return process.env.NEXT_PUBLIC_FORWARD_BROWSER_LOGS !== 'false';
};
